import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Check } from 'lucide-react';
import { useProfiles } from '../context/ProfileContext';

const ProfilePicker = () => {
  const { profiles, activeProfile, setActiveProfileId } = useProfiles();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Wanneer er nog geen profielen zijn: knop die naar setup leidt
  if (!profiles.length) {
    return (
      <button
        data-testid="profile-picker-add"
        onClick={() => navigate('/profielen')}
        className="rounded-full w-9 h-9 flex items-center justify-center bg-old-rose/15 hover:bg-old-rose/25 text-old-rose transition-colors"
        title="Profiel toevoegen"
        aria-label="Profiel toevoegen"
      >
        <Plus className="w-4 h-4" />
      </button>
    );
  }

  const avatarBubble = (avatar, color, size = 'w-9 h-9 text-xl') => (
    <div
      className={`${size} rounded-full flex items-center justify-center font-bold shadow-sm select-none transition-transform`}
      style={{ background: `${color}22`, border: `2px solid ${color}` }}
    >
      <span>{avatar}</span>
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="profile-picker"
        onClick={() => setOpen((o) => !o)}
        className="rounded-full hover:scale-105 active:scale-95 transition-transform"
        aria-label="Kies profiel"
      >
        {activeProfile
          ? avatarBubble(activeProfile.avatar, activeProfile.color || '#db8f2d')
          : <div className="w-9 h-9 rounded-full bg-muted/40 flex items-center justify-center text-xs">?</div>}
      </button>

      {open && (
        <div
          data-testid="profile-picker-popover"
          className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-2xl shadow-xl p-3 z-50"
        >
          <div className="text-xs font-accent text-muted-foreground mb-2 px-1">Voor wie lees je?</div>
          <div className="space-y-1.5">
            {profiles.map((p) => (
              <button
                key={p.id}
                data-testid={`profile-pick-${p.id}`}
                onClick={() => { setActiveProfileId(p.id); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-xl transition-colors ${
                  activeProfile?.id === p.id ? 'bg-gold/15' : 'hover:bg-muted/40'
                }`}
              >
                {avatarBubble(p.avatar, p.color || '#db8f2d', 'w-9 h-9 text-lg')}
                <div className="flex-1 text-left">
                  <div className="font-bold text-sm text-foreground">{p.name}</div>
                  {p.age_band && <div className="text-[10px] font-accent text-muted-foreground">{p.age_band} jaar</div>}
                </div>
                {activeProfile?.id === p.id && <Check className="w-4 h-4 text-gold" />}
              </button>
            ))}
          </div>
          <button
            data-testid="profile-picker-manage"
            onClick={() => { setOpen(false); navigate('/profielen'); }}
            className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-muted/30 hover:bg-muted/50 text-sm font-medium text-foreground transition-colors"
          >
            <Settings className="w-4 h-4" />
            Profielen beheren
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfilePicker;
